// src/pages/DebtStudio/components/ScaleBuilder/ShiftControls.jsx
import React from "react";

export default function ShiftControls({
  shiftPreset,
  setShiftPreset,
  shiftMagnitude,
  setShiftMagnitude,
  applyShift,
  undoShift,
  clearAdjustments,
  loading,
  prevExists,
}) {
  // match Adj(bps) highlight used in ScaleGrid:
  // base: "input w-20 text-right bg-app bg-opacity-100"
  // highlight: "bg-yellow-50 dark:bg-yellow-900/30"
  const magInputClass = "input w-24 text-right bg-app bg-opacity-100 bg-yellow-50 dark:bg-yellow-900/30";

  return (
    <div className="mb-3">
      {/* Controls row */}
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-app mr-2">Shift preset</label>

          {/* Styled select (rounded, dark-mode compatible) */}
          <div className="relative inline-block">
            <select
              aria-label="Select shift preset"
              className="appearance-none pr-9 pl-3 h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                         focus:outline-none focus:ring-2 focus:ring-ring max-w-full"
              value={shiftPreset}
              onChange={(e) => setShiftPreset(e.target.value)}
            >
              <option value="none">None</option>
              <option value="parallel_up">Parallel up</option>
              <option value="parallel_down">Parallel down</option>
              <option value="steepen">Steepen (short→long)</option>
              <option value="flatten">Flatten (short→long)</option>
              <option value="short_steepen">Short-end steepen</option>
              <option value="long_steepen">Long-end steepen</option>
            </select>

            {/* Chevron */}
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.2l3.71-2.97a.75.75 0 1 1 .94 1.17l-4.18 3.35a.75.75 0 0 1-.94 0L5.25 8.4a.75.75 0 0 1-.02-1.19z" />
            </svg>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-app mr-2">Magnitude (bps)</label>

          {/* Keyboard-only numeric input (no spinner), styled to match Adj(bps) highlight */}
          <input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            className={magInputClass}
            value={String(shiftMagnitude)}
            onChange={(e) => {
              // allow only digits (strip other chars), interpret empty as 0
              const cleaned = e.target.value.replace(/[^\d-]/g, "");
              setShiftMagnitude(cleaned === "" ? 0 : Number(cleaned));
            }}
            onFocus={(e) => e.target.select()}
            aria-label="Shift magnitude in bps"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            className="btn btn-primary"
            onClick={applyShift}
            disabled={shiftPreset === "none" || loading || shiftMagnitude === 0}
          >
            Apply Shift
          </button>
          <button className="btn btn-primary" onClick={undoShift} disabled={!prevExists}>
            Undo Last
          </button>
          <button className="btn btn-primary" onClick={clearAdjustments}>
            Clear
          </button>
        </div>
      </div>

      {/* Explanation always below the controls */}
      <div className="w-full text-sm text-muted mt-3">
        <strong>How shifts work:</strong>{" "}
        <span className="text-app">Apply</span> adds the selected preset and magnitude (in bps) to the current adjustments — shifts are stackable.
        <span className="mx-2">|</span>
        <span className="text-app">Undo</span> restores to the prior shift adjustments.
        <span className="mx-2">|</span>
        <span className="text-app">Clear</span> zeros all adjustments and clears the undo snapshot.
      </div>
    </div>
  );
}
